import { Collapse, IconButton, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../redux';
import {
  updateBarChartStyleOptions,
  updateHorizontalBarChartStyleOptions,
  updateStackedBarChartStyleOptions,
  updateHorizontalStackedBarChartStyleOptions,
} from '../../../redux/ChartPoperties/ChartControlsActions';
import { limitChartData } from '../../CommonFunctions/CommonFunctions';
import ChartColors from './ChartColors';
import useCombinedColorSchemes from '../../../hooks/useCombinedColorSchemes';
import { ColorSchemes } from './ColorScheme';
import CustomColorBuilder from './CustomColorBuilder';
import { OrientationRadioGroup } from '../Legend/LegendComponents/OrientationRadioGroup';
import ColorPickerPopover from './ChartColorComponents/ColorPickerPopover';

const BarColorControls = ({ iconColor }: { iconColor?: string }) => {
  const dispatch = useDispatch();

  const { chartControls, tabTileProps, chartProperties } = useSelector((state: RootState) => {
    return {
      chartControls: state.chartControls,
      tabTileProps: state.tabTileProps,
      chartProperties: state.chartProperties,
    };
  });

  const propKey = `${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`;
  const chartType = chartProperties.properties[propKey].chartType;

  let currentControls: any = {};
  let updateAction: Function | null = null;

  switch (chartType) {
    case 'multibar':
      currentControls = chartControls.properties[propKey].barChartControls || {};
      updateAction = updateBarChartStyleOptions;
      break;
    case 'horizontalBar':
      currentControls = chartControls.properties[propKey].horizontalBarChartControls || {};
      updateAction = updateHorizontalBarChartStyleOptions;
      break;
    case 'stackedBar':
      currentControls = chartControls.properties[propKey].stackedBarChartControls || {};
      updateAction = updateStackedBarChartStyleOptions;
      break;
    case 'horizontalStacked':
      currentControls = chartControls.properties[propKey].horizontalStackedBarChartControls || {};
      updateAction = updateHorizontalStackedBarChartStyleOptions;
      break;
    default:
      currentControls = {};
  }

  const updateStyleOptions = (propKey: string, option: string, value: any) => {
    if (updateAction) {
      dispatch(updateAction(propKey, option, value));
    }
  };

  const chartData = chartControls.properties[propKey].chartData || [];

  const allThemes = useCombinedColorSchemes();
  const currentSchemeName = chartControls.properties[propKey].colorScheme;

  const selectedScheme = allThemes.find((scheme) => scheme.name === currentSchemeName);
  const themeColors = selectedScheme?.colors || ColorSchemes[0].colors;

  const lastManualSchemeRef = useRef(currentSchemeName);

  useEffect(() => {
    if (currentControls.manualColor) {
      lastManualSchemeRef.current = currentSchemeName;
    }
  }, [currentControls.manualColor, currentSchemeName]);

  const chartAxes = chartProperties.properties[propKey].chartAxes;
  const dimensionFields = chartAxes[1]?.fields || [];
  const measureFields = chartAxes[3]?.fields || [];
  const hasSplitBy = chartAxes[2]?.fields?.length > 0;
  const measureName = measureFields[0]?.displayname || '';
  const limitedChartData = limitChartData(chartData, 100, measureName);

  const isSingleMeasureNoSplit = !hasSplitBy && measureFields.length === 1;
  const shouldForceIndividual = hasSplitBy || measureFields.length > 1;
  let allProcessedValues: string[] = [];
  let categoryLabel = '';
  let coloringFieldId = '';

  if (hasSplitBy) {
    const splitField = chartAxes[2].fields[0];
    const splitFieldName = splitField.displayname || splitField.fieldname;
    categoryLabel = splitFieldName;
    coloringFieldId = splitField.fieldname;

    const rawValues = limitedChartData.map((d: any) => {
      const val = d[splitFieldName];
      return val === null || val === 'null' ? '(Blank)' : String(val);
    });
    allProcessedValues = Array.from(new Set(rawValues));
  } else if (measureFields.length === 1 && dimensionFields.length > 0) {
    const lastDimension = dimensionFields[dimensionFields.length - 1];
    const lastDimName = lastDimension.displayname || lastDimension.fieldname;
    categoryLabel = lastDimName;
    coloringFieldId = lastDimension.fieldname;

    const rawValues = limitedChartData.map((d: any) => {
      const val = d[lastDimName];
      return val === null || val === 'null' ? '(Blank)' : String(val);
    });
    allProcessedValues = Array.from(new Set(rawValues));
  } else {
    categoryLabel = 'Measures';
    coloringFieldId = '__MEASURES_CONST_ID__';
    allProcessedValues = measureFields.map((f: any) => f.displayname);
  }

  const currentValuesKey = allProcessedValues.join('||');
  const prevFieldIdRef = useRef(coloringFieldId);
  const prevCategoryLabelRef = useRef(categoryLabel);
  const prevValuesKeyRef = useRef(currentValuesKey);

  useEffect(() => {
    const hasIdChanged = prevFieldIdRef.current && prevFieldIdRef.current !== coloringFieldId;
    const hasLabelChanged =
      prevCategoryLabelRef.current && prevCategoryLabelRef.current !== categoryLabel;
    const hasValuesChanged = prevValuesKeyRef.current !== currentValuesKey;
    if (
      hasIdChanged ||
      hasLabelChanged ||
      (coloringFieldId === '__MEASURES_CONST_ID__' && hasValuesChanged)
    ) {
      updateStyleOptions(propKey, 'nodeValuesColors', {});
    }

    prevFieldIdRef.current = coloringFieldId;
    prevCategoryLabelRef.current = categoryLabel;
    prevValuesKeyRef.current = currentValuesKey;
  }, [coloringFieldId, categoryLabel, currentValuesKey, propKey, dispatch, updateAction]);

  const displayValues = allProcessedValues.filter((val) => val !== '(Blank)');

  const manualKeys = Object.keys(currentControls.nodeValuesColors || {});
  const customValues = manualKeys.filter(
    (key) => !allProcessedValues.includes(key) && key !== 'null' && key !== '(Blank)',
  );

  const globalAllColor = currentControls.allSeriesColor;

  useEffect(() => {
    if (!currentControls.manualColor) return;

    if (isSingleMeasureNoSplit && !currentControls.allSeriesColor) {
      updateStyleOptions(propKey, 'allSeriesColor', themeColors[0]);
    }
    if (shouldForceIndividual && currentControls.allSeriesColor === themeColors[0]) {
      updateStyleOptions(propKey, 'allSeriesColor', '');
    }
  }, [
    currentControls.manualColor,
    isSingleMeasureNoSplit,
    shouldForceIndividual,
    currentControls.allSeriesColor,
    propKey,
    themeColors,
  ]);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedSeriesName, setSelectedSeriesName] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [isExpanded, setIsExpanded] = useState(true);

  const hasIndividualChanges = Object.keys(currentControls.nodeValuesColors || {}).length > 0;

  let hasGlobalChange = !!globalAllColor;

  if (isSingleMeasureNoSplit && globalAllColor === themeColors[0]) {
    hasGlobalChange = false;
  }

  const showReset = hasGlobalChange || hasIndividualChanges;

  const handleColorChange = (color: string) => {
    if (selectedSeriesName === 'ALL_SERIES_COLOR') {
      updateStyleOptions(propKey, 'allSeriesColor', color);
      updateStyleOptions(propKey, 'nodeValuesColors', {});
    } else {
      const currentMap = currentControls.nodeValuesColors || {};
      const newMap = { ...currentMap, [selectedSeriesName]: color };
      updateStyleOptions(propKey, 'nodeValuesColors', newMap);
    }
  };

  const handleAddCustomColor = (name: string, color: string) => {
    const currentMap = currentControls.nodeValuesColors || {};
    const newMap = { ...currentMap, [name]: color };
    updateStyleOptions(propKey, 'nodeValuesColors', newMap);
  };

  const handleRemoveColorRule = (name: string) => {
    const currentMap = { ...(currentControls.nodeValuesColors || {}) };
    delete currentMap[name];
    updateStyleOptions(propKey, 'nodeValuesColors', currentMap);
  };

  const handleRemoveAllCustomColors = () => {
    const currentMap = { ...(currentControls.nodeValuesColors || {}) };
    customValues.forEach((key) => {
      delete currentMap[key];
    });
    updateStyleOptions(propKey, 'nodeValuesColors', currentMap);
  };

  const handleFullReset = (e: React.MouseEvent) => {
    e.stopPropagation();
    updateStyleOptions(propKey, 'allSeriesColor', '');
    updateStyleOptions(propKey, 'nodeValuesColors', {});
  };

  const handleOpenPicker = (e: React.MouseEvent<HTMLElement>, name: string, color: string) => {
    setSelectedSeriesName(name);
    setSelectedColor(color);
    setAnchorEl(e.currentTarget);
  };

  const renderAllControl = () => {
    const displayColor = globalAllColor || '#ffffff';

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          marginTop: 8,
          marginBottom: 12,
          width: '100%',
        }}
      >
        <div
          style={{
            height: '1rem',
            width: '30px',
            minWidth: '30px',
            backgroundColor: displayColor,
            border: '1px solid darkgray',
            cursor: 'pointer',
            marginRight: '10px',
            borderRadius: '2px',
            backgroundImage: !globalAllColor
              ? 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)'
              : 'none',
            backgroundSize: !globalAllColor ? '10px 10px' : 'auto',
            backgroundPosition: !globalAllColor ? '0 0, 0 5px, 5px -5px, -5px 0px' : '0 0',
          }}
          onClick={(e) => handleOpenPicker(e, 'ALL_SERIES_COLOR', globalAllColor || '#000000')}
        />

        <label
          style={{
            fontSize: '13px',
            fontWeight: 600,
            marginLeft: '5px',
            flexGrow: 1,
            cursor: 'default',
            textAlign: 'left',
            color: iconColor ? iconColor : '#404040',
          }}
        >
          All
        </label>

        {showReset && (
          <IconButton
            size="small"
            onClick={handleFullReset}
            sx={{
              padding: '2px',
              marginLeft: '5px',
              color: iconColor || 'lightgray',
              '&:hover': {
                color: iconColor || 'gray',
                backgroundColor: 'transparent',
              },
            }}
            title="Reset All Manual Changes"
          >
            <RestartAltIcon style={{ fontSize: 16, color: 'gray' }} />
          </IconButton>
        )}
      </div>
    );
  };

  const renderRow = (itemName: string, isCustomList: boolean) => {
    const manualMap = currentControls.nodeValuesColors || {};
    const dataIndex = allProcessedValues.indexOf(itemName);

    let defaultBaseColor = '#cccccc';

    if (dataIndex >= 0) {
      if (globalAllColor) {
        defaultBaseColor = globalAllColor;
      } else {
        if (isSingleMeasureNoSplit) {
          defaultBaseColor = themeColors[0];
        } else {
          defaultBaseColor = themeColors[dataIndex % themeColors.length];
        }
      }
    }

    const displayColor = manualMap[itemName] || defaultBaseColor;
    const isFutureValue = isCustomList;
    const labelText = itemName === 'null' ? '(Blank)' : itemName;

    return (
      <div
        key={itemName}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          marginTop: 8,
          width: '100%',
        }}
      >
        <div
          style={{
            height: '1rem',
            width: '30px',
            minWidth: '30px',
            backgroundColor: displayColor,
            border: '1px solid darkgray',
            cursor: 'pointer',
            marginRight: '10px',
            borderRadius: '2px',
          }}
          onClick={(e) => handleOpenPicker(e, itemName, displayColor)}
        />

        <label
          style={{
            fontSize: '12px',
            marginLeft: '5px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: isFutureValue ? '#2bb9bb' : iconColor || 'inherit',
            cursor: 'default',
            flexGrow: 1,
            textAlign: 'left',
          }}
          title={isFutureValue ? `${labelText} (Custom Value)` : labelText}
        >
          {labelText}
        </label>
      </div>
    );
  };

  const renderColorControls = () => {
    return (
      <div
        style={{ marginBottom: '10px', borderBottom: '1px solid #f0f0f0', paddingBottom: '5px' }}
      >
        <div
          className="optionDescription"
          style={{ marginTop: '0', display: 'flex', justifyContent: 'space-around' }}
        >
          <label
            style={{
              width: '100%',
              fontWeight: 600,
              fontSize: '13px',
              color: iconColor ? iconColor : '#404040',
            }}
          >
            {categoryLabel}
          </label>
          <IconButton
            size="small"
            onClick={() => setIsExpanded(!isExpanded)}
            style={{ padding: 2 }}
            title={isExpanded ? 'Collapse List' : 'Expand List'}
          >
            {isExpanded ? (
              <KeyboardArrowUpIcon
                fontSize="small"
                sx={{ color: iconColor ? iconColor : '#404040' }}
              />
            ) : (
              <KeyboardArrowDownIcon
                fontSize="small"
                sx={{ color: iconColor ? iconColor : '#404040' }}
              />
            )}
          </IconButton>
        </div>

        <Collapse in={isExpanded}>
          <div
            style={{
              marginTop: '5px',
              paddingLeft: '10px',
              paddingBottom: '10px',
            }}
          >
            {renderAllControl()}
            {displayValues.map((val) => renderRow(val, false))}
          </div>
        </Collapse>

        <div
          style={{
            marginTop: '15px',
            padding: '10px 10px 0px',
            borderTop: '1px dashed #e0e0e0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <Typography variant="caption" style={{ color: iconColor || '#888', fontWeight: 600 }}>
            Default for Blank / Empty
          </Typography>
          {renderRow('(Blank)', false)}
        </div>

        <CustomColorBuilder
          onAdd={handleAddCustomColor}
          existingColors={currentControls.nodeValuesColors || {}}
          onRemove={handleRemoveColorRule}
          onRemoveAll={handleRemoveAllCustomColors}
          customList={customValues}
          colorMap={currentControls.nodeValuesColors || {}}
          colorPalette={themeColors}
          onColorChange={handleAddCustomColor}
          iconColor={iconColor}
        />
      </div>
    );
  };

  return (
    <div style={{ marginTop: '20px', paddingTop: '8px', paddingLeft: '4px' }}>
      <OrientationRadioGroup
        orientation={currentControls.manualColor ? 'manual' : 'auto'}
        options={[
          { name: 'Auto', key: 'auto' },
          { name: 'Manual', key: 'manual' },
        ]}
        onChange={(key) => {
          const isSwitchingToManual = key === 'manual';

          if (isSwitchingToManual) {
            // Checking if scheme changed since we were last in manual
            if (currentSchemeName !== lastManualSchemeRef.current) {
              updateStyleOptions(propKey, 'allSeriesColor', '');
              updateStyleOptions(propKey, 'nodeValuesColors', {});
            }
            updateStyleOptions(propKey, 'manualColor', true);
          } else {
            updateStyleOptions(propKey, 'manualColor', false);
          }
        }}
        style={{ width: '94%' }}
      />

      <div>
        {currentControls.manualColor ? (
          <>
            <div style={{ marginBottom: '10px', paddingRight: '10px' }}>
              {renderColorControls()}
            </div>
            <ColorPickerPopover
              isOpen={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              currentColor={selectedColor}
              onColorChange={handleColorChange}
              propKey={propKey}
              selectedMenu={''}
            />
          </>
        ) : (
          <div style={{ marginTop: '-15px' }}>
            <ChartColors iconColor={iconColor} />
          </div>
        )}
      </div>
    </div>
  );
};

export default BarColorControls;
